/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */

export const installationContent = `
## Summary

This guide walks you through installing and running Dexter on a Windows machine using Docker containers. This guide assumes the environment has no internet connection, so all Docker images must be pre-loaded from \`.tar\` files.

Dexter runs as a multi-container application consisting of:
- **MongoDB** - Database for storing collections, files, and mappings
- **Dexter App** - Main application server and web interface
- **ML Service** - Machine learning service for file type detection
- **Parser Service** - File parsing and data extraction service

This installation assumes Docker Desktop is already installed and running on your Windows machine.

---

## Prerequisites

Before beginning installation, ensure you have:

**Required Software:**
- Windows 10/11 (64-bit)
- Docker Desktop for Windows (installed and running)
- Administrator privileges (for Docker commands)

**Required Files:**
You should have received four Docker image files:
- \`dexter-mongo-local.tar\` (MongoDB database)
- \`dexter-app-local.tar\` (Dexter application)
- \`dexter-ml-local.tar\` (ML service)
- \`dexter-parser-local.tar\` (Parser service)

**Verify Docker is Running:**
Open Command Prompt and run:
\`\`\`
docker --version
\`\`\`
You should see Docker version information. If not, start Docker Desktop and wait for it to fully initialize.

---

## Installation Overview

The installation process follows these steps:

1. **Create Docker Network** - Allows containers to communicate
2. **Load Docker Images** - Import the four .tar files
3. **Run MongoDB Container** - Start the database
4. **Run ML Service Container** - Start file type detection service
5. **Run Parser Service Container** - Start file parsing service
6. **Run Dexter App Container** - Start the main application
7. **Verify Installation** - Check all containers are running
8. **Access Dexter** - Open in web browser

---

## Step 1: Create Docker Network

Docker containers need a network to communicate with each other.

**Check if Network Exists:**
\`\`\`bash
docker network ls
\`\`\`

Look for a network named \`dexter-network\`. If it doesn't exist, create it:

**Create Network:**
\`\`\`bash
docker network create dexter-network
\`\`\`

**Verify Creation:**
\`\`\`bash
docker network ls
\`\`\`

You should now see \`dexter-network\` in the list.

---

## Step 2: Install MongoDB Container

The MongoDB container stores all Dexter data including collections, files, and transformation mappings.

### Load MongoDB Image

Navigate to the directory containing \`dexter-mongo-local.tar\` and run:

\`\`\`bash
docker load -i dexter-mongo-local.tar
\`\`\`

**Expected Output:**
\`\`\`
Loaded image: simventions.azurecr.io/ironbank/bitnami/mongodb:5.0.9
\`\`\`

This may take 2-3 minutes depending on your system.

### Run MongoDB Container

\`\`\`bash
docker run -d ^
  --name mongodb ^
  --network dexter-network ^
  -p 27017:27017 ^
  -e MONGODB_ROOT_USER=dexter-root ^
  -e MONGODB_ROOT_PASSWORD=devdb ^
  -e MONGODB_REPLICA_SET_MODE=primary ^
  -e MONGODB_REPLICA_SET_NAME=dexter-dev-dbset ^
  -e MONGODB_REPLICA_SET_KEY=devReplicasetKey ^
  -e MONGODB_ADVERTISED_HOSTNAME=localhost ^
  -v mongodb-data:/bitnami/mongodb ^
  --restart unless-stopped ^
  simventions.azurecr.io/ironbank/bitnami/mongodb:5.0.9
\`\`\`

**Command Explanation:**
- \`-d\` - Run in detached mode (background)
- \`--name mongodb\` - Name the container "mongodb"
- \`--network dexter-network\` - Connect to Dexter network
- \`-p 27017:27017\` - Expose MongoDB port
- \`-e\` - Set environment variables for database configuration
- \`-v mongodb-data:/bitnami/mongodb\` - Persist data in Docker volume
- \`--restart unless-stopped\` - Auto-restart container if it crashes

### Verify MongoDB is Running

\`\`\`bash
docker ps | findstr mongodb
\`\`\`

You should see the mongodb container with status "Up".

**Check MongoDB Logs:**
\`\`\`bash
docker logs mongodb
\`\`\`

Look for messages indicating successful startup. You should see no error messages.

---

## Step 3: Install ML Service Container

The ML service provides AI-powered file type detection.

### Load ML Service Image

\`\`\`bash
docker load -i dexter-ml-local.tar
\`\`\`

**Expected Output:**
\`\`\`
Loaded image: ml-service:latest
\`\`\`

### Run ML Service Container

\`\`\`bash
docker run -d ^
  --name dexter-ml ^
  --network dexter-network ^
  -p 5000:5000 ^
  -e ML_SERVICE_HOSTNAME=dexter-ml ^
  -e ML_SERVICE_PORT=5000 ^
  ml-service:latest
\`\`\`

**Command Explanation:**
- \`--name dexter-ml\` - Name the container "dexter-ml"
- \`-p 5000:5000\` - Expose ML service on port 5000
- Environment variables configure how other containers connect

### Verify ML Service is Running

\`\`\`bash
docker ps | findstr dexter-ml
\`\`\`

Container should show status "Up".

---

## Step 4: Install Parser Service Container

The parser service extracts structured data from uploaded files.

### Load Parser Service Image

\`\`\`bash
docker load -i dexter-parser-local.tar
\`\`\`

**Expected Output:**
\`\`\`
Loaded image: parse-service:latest
\`\`\`

### Run Parser Service Container

\`\`\`bash
docker run -d ^
  --name dexter-parsing-instance ^
  --network dexter-network ^
  -p 5001:5001 ^
  -e PARSE_SERVICE_HOSTNAME=dexter-parsing-instance ^
  -e PARSE_SERVICE_PORT=5001 ^
  parse-service:latest
\`\`\`

**Command Explanation:**
- \`--name dexter-parsing-instance\` - Name the container
- \`-p 5001:5001\` - Expose parser service on port 5001
- Environment variables configure connectivity

### Verify Parser Service is Running

\`\`\`bash
docker ps | findstr dexter-parsing
\`\`\`

Container should show status "Up".

---

## Step 5: Install Dexter Application Container

The main Dexter application provides the web interface and transformation engine.

### Load Dexter App Image

\`\`\`bash
docker load -i dexter-app-local.tar
\`\`\`

**Expected Output:**
\`\`\`
Loaded image: simventions.azurecr.io/dexter-master:13245
\`\`\`

This is the largest image and may take 3-5 minutes to load.

### Run Dexter App Container

\`\`\`bash
docker run -d ^
  --name dexter-app ^
  --network dexter-network ^
  -p 8082:8080 ^
  -v dexter-app-data:/dexter-data ^
  -e DEXTER_MONGO_USERNAME=dexter-root ^
  -e DEXTER_MONGO_PASSWORD=devdb ^
  -e DEXTER_MONGO_HOSTNAME=mongodb ^
  -e ML_SERVICE_HOSTNAME=dexter-ml ^
  -e ML_SERVICE_PORT=5000 ^
  -e PARSE_SERVICE_HOSTNAME=dexter-parsing-instance ^
  -e PARSE_SERVICE_PORT=5001 ^
  simventions.azurecr.io/dexter-master:13245
\`\`\`

**Command Explanation:**
- \`--name dexter-app\` - Name the container "dexter-app"
- \`-p 8082:8080\` - Expose Dexter on port 8082 (maps to internal port 8080)
- \`-v dexter-app-data:/dexter-data\` - Persist uploaded files and data
- Environment variables connect to MongoDB, ML, and Parser services

### Verify Dexter App is Running

\`\`\`bash
docker ps | findstr dexter-app
\`\`\`

Container should show status "Up".

**Wait for Startup:**
The Dexter app needs 30-60 seconds to fully initialize. Wait before accessing.

---

## Step 6: Verify All Containers

Check that all four containers are running successfully:

\`\`\`bash
docker ps
\`\`\`

You should see four containers all with status "Up":
- \`mongodb\`
- \`dexter-ml\`
- \`dexter-parsing-instance\`
- \`dexter-app\`

**Verify Container Health:**
\`\`\`bash
docker ps --format "table {{.Names}}\t{{.Status}}\t{{.Ports}}"
\`\`\`

All containers should show "Up" status with their respective ports.

---

## Step 7: Access Dexter

### Open Dexter in Browser

1. Open **Google Chrome** (recommended) or Microsoft Edge
2. Navigate to: **http://localhost:8082**
3. Dexter's login page should appear

**If page doesn't load immediately:**
- Wait 30 more seconds (app may still be initializing)
- Check Docker Desktop - all containers should be green/running
- Refresh the browser

### Initialize Database

**First-Time Setup Only:**

When you first access Dexter, you may see an **Initialize** button:

1. Click the **Initialize** button
2. Success message appears
3. Login screen displays

### Log In to Dexter

1. Click on the available user account (displayed on screen)
2. You should be logged in and see the Dexter home page
3. Successful login confirms:
   - MongoDB connection is working
   - Application is properly configured
   - All services are communicating

**You're now ready to use Dexter!**



`;
